import React, { FunctionComponent, useState } from 'react';
import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonListHeader,
  IonModal,
  IonRadio,
  IonRadioGroup,
  IonTitle,
  IonToolbar,
} from '@ionic/react';
import { close, arrowBack, add } from 'ionicons/icons';
import { MetaLibraryConstituentModel } from '../models/meta-library.model';
import { EditActionButton } from '../../shared/components/edit-action-button.component';

interface CharacteristicsModalProps {
  isOpen: boolean;
  constituent: MetaLibraryConstituentModel | null;
  constituentCode: string;
  onClose: () => void;
  onBack: () => void;
  onValidate?: (lineCode: string, columnCode: string) => void;
}

export const CharacteristicsModal: FunctionComponent<CharacteristicsModalProps> = ({
  isOpen,
  constituent,
  constituentCode,
  onClose,
  onBack,
  onValidate,
}) => {
  const [selectedLineCode, setSelectedLineCode] = useState<string>('');
  const [selectedColumnCode, setSelectedColumnCode] = useState<string>('');

  // Extraire les valeurs uniques pour chaque liste
  const getUniqueLineValues = () => {
    if (!constituent) return [];
    const uniqueMap = new Map<string, string>();
    
    constituent.metaLibraryCharacteristics.forEach((char) => {
      if (char.lineValue && char.code.length >= 1) {
        const lineCode = char.code.charAt(0);
        if (!uniqueMap.has(lineCode)) {
          uniqueMap.set(lineCode, char.lineValue);
        }
      }
    });
    
    return Array.from(uniqueMap.entries()).map(([code, value]) => ({
      code,
      value,
    }));
  };

  const getUniqueColumnValues = () => {
    if (!constituent) return [];
    const uniqueMap = new Map<string, string>();
    
    constituent.metaLibraryCharacteristics.forEach((char) => {
      if (char.columnValue && char.code.length >= 2) {
        const columnCode = char.code.charAt(1);
        if (!uniqueMap.has(columnCode)) {
          uniqueMap.set(columnCode, char.columnValue);
        }
      }
    });
    
    return Array.from(uniqueMap.entries()).map(([code, value]) => ({
      code,
      value,
    }));
  };

  const lineValues = getUniqueLineValues();
  const columnValues = getUniqueColumnValues();

  const handleValidate = () => {
    if (onValidate && selectedLineCode && selectedColumnCode) {
      onValidate(selectedLineCode, selectedColumnCode);
    }
  };

  return (
    <IonModal isOpen={isOpen}>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonButton onClick={onBack}>
              <IonIcon icon={arrowBack} />
            </IonButton>
          </IonButtons>
          <IonTitle>Caractéristiques</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={onClose}>
              <IonIcon icon={close} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        {constituent && (
          <>
            <div style={{ padding: '16px 0', marginBottom: '16px' }}>
              <p style={{ 
                fontSize: '14px', 
                color: 'var(--ion-color-medium)', 
                marginBottom: '12px',
                fontStyle: 'italic'
              }}>
                Sélectionnez les caractéristiques de l'équipement
              </p>
              <h2 style={{ 
                fontSize: '18px', 
                fontWeight: 'bold',
                margin: 0
              }}>
                {constituentCode} - {constituent.constituent}
              </h2>
            </div>

            {/* Liste 1 - Ligne */}
            {constituent.lineLabel && lineValues.length > 0 && (
              <IonList>
                <IonListHeader>
                  <IonLabel>
                    <h3 style={{ fontWeight: 'bold' }}>{constituent.lineLabel}</h3>
                  </IonLabel>
                </IonListHeader>
                <IonRadioGroup
                  value={selectedLineCode}
                  onIonChange={(e) => setSelectedLineCode(e.detail.value)}
                >
                  {lineValues.map((item) => (
                    <IonItem 
                      key={item.code}
                      button
                      onClick={() => setSelectedLineCode(item.code)}
                    >
                      <IonRadio slot="start" value={item.code} />
                      <IonLabel>
                        {item.code} - {item.value}
                      </IonLabel>
                    </IonItem>
                  ))}
                </IonRadioGroup>
              </IonList>
            )}

            {/* Liste 2 - Colonne */}
            {constituent.columnLabel && columnValues.length > 0 && (
              <IonList style={{ marginTop: '16px' }}>
                <IonListHeader>
                  <IonLabel>
                    <h3 style={{ fontWeight: 'bold' }}>{constituent.columnLabel}</h3>
                  </IonLabel>
                </IonListHeader>
                <IonRadioGroup
                  value={selectedColumnCode}
                  onIonChange={(e) => setSelectedColumnCode(e.detail.value)}
                >
                  {columnValues.map((item) => (
                    <IonItem 
                      key={item.code}
                      button
                      onClick={() => setSelectedColumnCode(item.code)}
                    >
                      <IonRadio slot="start" value={item.code} />
                      <IonLabel>
                        {item.code} - {item.value}
                      </IonLabel>
                    </IonItem>
                  ))}
                </IonRadioGroup>
              </IonList>
            )}

            {/* Bouton de validation */}
            <div style={{ padding: '16px 0', marginTop: '24px' }}>
              <EditActionButton
                onClick={handleValidate}
                icon={add}
                disabled={!selectedLineCode || !selectedColumnCode}
                label="Ajouter le type d'équipement"
                color="secondary"
              />
            </div>
          </>
        )}
      </IonContent>
    </IonModal>
  );
};

